/**
 * Per-kind quest rules. The descriptor controls UI behavior (which fields
 * are editable per-card, whether cards lock once running, etc.) and the
 * runner reads it to enforce mutability at the boundary.
 *
 * v1 ships two kinds:
 *
 *   - "loop"  → strict ralph: one role + one model picked at setup, summarizer
 *               optional, cards locked once running.
 *   - "ralph" → verify-loop: role/model locked entirely (worker + verifier
 *               pair from the quest defaults), summarizer off (verifier is
 *               the natural completion signal), cards locked once running.
 *
 * Future variants (loop-deluxe, handoff, committee) flip the FieldMode flags
 * and `cardsEditableWhileRunning` without changing the panel shell.
 */

import type { QuestKind, QuestRules } from "./types.js";

const LOOP_RULES: QuestRules = {
  perCardRole: "inherit",
  perCardModel: "inherit",
  perCardPrompt: "locked",
  cardsEditableWhileRunning: false,
  summarizerCard: "optional",
  cardExpansion: "tool-calls",
};

const RALPH_RULES: QuestRules = {
  perCardRole: "locked",
  perCardModel: "locked",
  perCardPrompt: "locked",
  cardsEditableWhileRunning: false,
  summarizerCard: "off",
  cardExpansion: "tool-calls",
};

/**
 * "run" — count-bounded like loop, but each card is pre-configured with its
 * own role + model. No summarizer, no per-card prompt.
 */
const RUN_RULES: QuestRules = {
  perCardRole: "free",
  perCardModel: "free",
  perCardPrompt: "locked",
  cardsEditableWhileRunning: false,
  summarizerCard: "off",
  cardExpansion: "tool-calls",
};

/**
 * "orchestrate" — wave-bounded. Each card carries a role + model pre-picked
 * by the user; the queen bee decides dispatch order and parallelism at runtime
 * by reading the hivemind doc. Per-card prompts are written by the queen at
 * dispatch time, not by the user up front.
 */
const ORCHESTRATE_RULES: QuestRules = {
  perCardRole: "free",
  perCardModel: "free",
  perCardPrompt: "locked",
  cardsEditableWhileRunning: false,
  summarizerCard: "off",
  cardExpansion: "tool-calls",
};

export function getRulesForKind(kind: QuestKind): QuestRules {
  switch (kind) {
    case "loop":
      return { ...LOOP_RULES };
    case "ralph":
      return { ...RALPH_RULES };
    case "run":
      return { ...RUN_RULES };
    case "orchestrate":
      return { ...ORCHESTRATE_RULES };
  }
}
